import { useCallback, useMemo, useState } from "react";

import { useParams } from "react-router-dom";

import {
  TaskTypeV3_1,
  TaskV3_1,
} from "@fartherfinance/frontend/api/Tasks/Types";
import useTransitionTasks from "@fartherfinance/frontend/api/TransitionWorkflows/hooks/useTransitionTasks";

import { SortOrder } from "../ui/TransitionTasksTable/types";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

import { useRetry } from "./useRetry";

type TaskFilterType = "onboarding-tasks" | "transition-tasks";

interface UseFilteredTransitionTasksResult {
  sortedTasks: TaskV3_1[];
  isLoading: boolean;
  hasError: boolean;
  sortBy: string | undefined;
  sortOrder: SortOrder;
  handleSort: (column: string, order: SortOrder) => void;
  handleRetry: () => void;
  paginationProps: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
  };
  sortingProps: {
    sortBy: string | undefined;
    sortOrder: SortOrder;
  };
}

const sortTasks = (
  tasks: TaskV3_1[],
  sortBy: string | undefined,
  sortOrder: SortOrder
): TaskV3_1[] => {
  if (!sortBy) {
    return tasks;
  }

  return [...tasks].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortBy) {
      case "title":
        aValue = a.title;
        bValue = b.title;
        break;
      case "taskStatus":
        aValue = a.taskStatus;
        bValue = b.taskStatus;
        break;
      case "dueDate":
        aValue = a.dueDate;
        bValue = b.dueDate;
        break;
      case "description":
        aValue = a.description || "";
        bValue = b.description || "";
        break;
      default:
        return 0;
    }

    // Handle null/undefined values
    if (aValue == null && bValue == null) {
      return 0;
    }
    if (aValue == null) {
      return sortOrder === "asc" ? 1 : -1;
    }
    if (bValue == null) {
      return sortOrder === "asc" ? -1 : 1;
    }

    // Handle date comparison
    if (sortBy === "dueDate") {
      const aTime =
        aValue instanceof Date ? aValue.getTime() : new Date(aValue).getTime();
      const bTime =
        bValue instanceof Date ? bValue.getTime() : new Date(bValue).getTime();
      return sortOrder === "asc" ? aTime - bTime : bTime - aTime;
    }

    // Handle string comparison
    const comparison = String(aValue).localeCompare(String(bValue));
    return sortOrder === "asc" ? comparison : -comparison;
  });
};

const filterTasks = (
  tasks: TaskV3_1[],
  rootAdvisorId: string | undefined,
  filterType: TaskFilterType
): TaskV3_1[] => {
  if (filterType === "onboarding-tasks") {
    return tasks
      .filter(
        (task) =>
          task.taskType === TaskTypeV3_1.enum.TRANSITION_ADVISOR_ONBOARDING
      )
      .filter((task) => task.assignee && task.assignee.id === rootAdvisorId);
  }

  if (filterType === "transition-tasks") {
    return tasks.filter(
      (task) =>
        task.taskType === TaskTypeV3_1.enum.TRANSITION_LPOA ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_ACATS ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_MASTER_MERGE ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_KICKOFF ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_LAUNCH ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_POST_LAUNCH ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_TRANSITION ||
        task.taskType === TaskTypeV3_1.enum.TRANSITION_ACCESS
    );
  }

  return tasks;
};

export const useFilteredTransitionTasks = (
  filterType: TaskFilterType,
  defaultSortBy: string = "dueDate"
): UseFilteredTransitionTasksResult => {
  const [sortBy, setSortBy] = useState<string | undefined>(defaultSortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  const auth = useAdvisorRequestAuth();
  const { transitionId } = useParams<{ transitionId: string }>();
  const { handleRetry } = useRetry("tasks");
  const {
    data: transitionTasks,
    isLoading,
    hasError,
  } = useTransitionTasks(transitionId!, auth);

  const level1And2Tasks = useMemo(() => {
    if (!transitionTasks?.level2Tasks || !transitionTasks?.level1Tasks) {
      return [];
    }
    // Combine level1 and level2 tasks into a single array
    return [...transitionTasks.level2Tasks, ...transitionTasks.level1Tasks];
  }, [transitionTasks?.level2Tasks, transitionTasks?.level1Tasks]);

  // Extract, filter, and sort level2Tasks
  const sortedTasks = useMemo(() => {
    if (!level1And2Tasks.length || !transitionTasks?.rootTask) {
      return [];
    }

    const rootAdvisorId = transitionTasks.rootTask.advisor?.id;
    const filteredTasks = filterTasks(
      level1And2Tasks,
      rootAdvisorId,
      filterType
    );
    const sortedTasksList = sortTasks(filteredTasks, sortBy, sortOrder);

    return sortedTasksList;
  }, [
    transitionTasks?.rootTask,
    level1And2Tasks,
    filterType,
    sortBy,
    sortOrder,
  ]);

  const handleSort = useCallback((column: string, order: SortOrder) => {
    setSortBy(column);
    setSortOrder(order);
  }, []);

  const paginationProps = useMemo(
    () => ({
      currentPage: 0,
      pageSize: sortedTasks.length,
      totalCount: sortedTasks.length,
    }),
    [sortedTasks.length]
  );

  const sortingProps = useMemo(
    () => ({
      sortBy,
      sortOrder,
    }),
    [sortBy, sortOrder]
  );

  return {
    sortedTasks,
    isLoading,
    hasError,
    sortBy,
    sortOrder,
    handleSort,
    handleRetry: handleRetry,
    paginationProps,
    sortingProps,
  };
};
